import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { question as questionConstant } from './constants';
import { getYouTubeVideoId } from '../../utils';

const useStyles = makeStyles()((theme) => ({
    mediaContainer: {
        width: '100%',
        height: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        padding: theme.spacing(2),
        boxSizing: 'border-box',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1),
        },
    },
    mediaElement: {
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
        overflow: 'hidden',
    },
    loadingContainer: {
        width: '100%',
        textAlign: 'center',
    },
    audioWrapper: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            gap: theme.spacing(1),
        },
    },
    audioPlaceholder: {
        width: '80px',
        height: '80px',
        backgroundColor: '#ddd',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '18px',
        color: '#888',
        [theme.breakpoints.down('sm')]: {
            width: '60px',
            height: '60px',
        },
    },
    audioPlayer: {
        width: '100%',
        maxWidth: '600px',
        [theme.breakpoints.down('sm')]: {
            width: '90%',
        },
    },
}));

export const MediaPreview = ({ contentURL, contentType, handleClose, mediaPreview, BackdropProps }) => {
    const { classes } = useStyles();
    const [loading, setLoading] = useState(true);

    const dialogTitle = questionConstant?.STYLES[contentType] || 'Preview';
    const fileName = contentURL?.split('/').pop();

    const renderAudioContent = () => (
        <Box className={classes.audioWrapper}>
            <Box className={classes.audioPlaceholder}>
                🎵 {/* You can replace this emoji with a dynamic image or album cover */}
            </Box>
            <Typography variant="body2">Audio - {fileName}</Typography>
            <audio
                controls
                controlsList="nodownload"
                className={classes.audioPlayer}
                onLoadedData={() => setLoading(false)}
                onLoadStart={() => setLoading(true)}
            >
                <source src={contentURL} type="audio/mpeg" />
                Your browser does not support the audio element.
            </audio>
        </Box>
    );

    const renderMediaContent = () => {
        if (contentType === 'text-and-image') {
            return (
                <img
                    src={contentURL}
                    alt="Image Preview"
                    className={classes.mediaElement}
                    onLoad={() => setLoading(false)}
                    onLoadStart={() => setLoading(true)}
                />
            );
        } else if (contentType === 'text-and-video') {
            const isYouTubeUrl = contentURL?.includes('youtube.com') || contentURL?.includes('youtu.be');

            if (isYouTubeUrl) {
                const videoId = getYouTubeVideoId(contentURL);

                return (
                    <iframe
                        width="100%"
                        height="315"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        onLoad={() => setLoading(false)}
                        className={classes.mediaElement}
                    />
                );
            } else {
                return (
                    <video
                        controls
                        controlsList="nodownload"
                        className={classes.mediaElement}
                        onLoadedData={() => setLoading(false)}
                        onLoadStart={() => setLoading(true)}
                    >
                        <source src={contentURL} type="video/mp4" />
                        Your browser does not support the video element.
                    </video>
                );
            }
        } else if (contentType === 'text-and-audio') {
            return renderAudioContent();
        }
        return null;
    };

    return (
        <Dialog open={mediaPreview} onClose={handleClose} fullWidth
            BackdropProps={BackdropProps}>
            <DialogTitle>{dialogTitle} Preview</DialogTitle>
            <DialogContent className={classes.mediaContainer}>
                {loading && <Box className={classes.loadingContainer}>
                    <CircularProgress />
                </Box>}
                {contentURL && renderMediaContent()}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}